import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Per-connection sync configuration (ADR 0018). The backend already
 * resolves server-side defaults — fresh connections get `'status'`
 * mode on `[11, 9]`. UI just renders what the API returns.
 *
 * `available_statuses` is the live list from BigCommerce's
 * `/v2/order_statuses`; populated on GET, omitted from PATCH responses
 * (UI already has them from the load).
 */
export type SyncMode = 'status' | 'automatic' | 'manual';

export interface AvailableStatus {
  readonly id: number;
  readonly name: string;
}

export interface SyncConfig {
  readonly sync_mode: SyncMode;
  readonly sync_trigger_statuses: readonly number[];
  readonly available_statuses: readonly AvailableStatus[];
}

export interface UpdatedSyncConfig {
  readonly sync_mode: SyncMode;
  readonly sync_trigger_statuses: readonly number[];
  readonly bc_webhook_ids: readonly string[];
}

export interface PatchSyncConfigInput {
  readonly sync_mode?: SyncMode;
  readonly sync_trigger_statuses?: readonly number[];
}

export function getSyncConfig(connectionId: string): Promise<Result<SyncConfig, ApiError>> {
  return apiRequest<SyncConfig>({
    method: 'GET',
    path: `/stores/${encodeURIComponent(connectionId)}/sync-config`,
  });
}

export function patchSyncConfig(
  connectionId: string,
  input: PatchSyncConfigInput,
): Promise<Result<UpdatedSyncConfig, ApiError>> {
  return apiRequest<UpdatedSyncConfig>({
    method: 'PATCH',
    path: `/stores/${encodeURIComponent(connectionId)}/sync-config`,
    body: input,
  });
}

export const SYNC_CONFIG_QUERY_KEY = (connectionId: string) =>
  ['sync-config', connectionId] as const;
